import type { GoalDto } from '@iac/contracts';
import { monthValue, plnAmount, plnMonth } from '@iac/contracts';
import { useState, type FormEvent } from 'react';
import { DataTable, Td, Tr } from '../../components/DataTable';
import { DeleteButton } from '../../components/DeleteButton';
import { Dialog } from '../../components/Dialog';
import { Field } from '../../components/Field';
import { IconPencilSquare, IconPlus } from '../../components/icons';
import { PageHeader } from '../../components/PageHeader';
import { useDict } from '../../i18n/dictionary';
import { useLocale } from '../../lib/locale';
import { usePageTitle } from '../../lib/page';
import { useCreateGoal, useDeleteGoal, useGoals, useUpdateGoal } from './api';
import { financesDict } from './i18n';

type GoalValues = { name: string; targetAmount: string; targetDate: string };

/** Adding and editing alike; `goal` seeds the fields once. */
function GoalForm({
  goal,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  goal?: GoalDto;
  submitLabel: string;
  pending: boolean;
  onSubmit: (values: GoalValues) => void;
  onCancel: () => void;
}) {
  const t = useDict(financesDict);
  const [name, setName] = useState(goal?.name ?? '');
  const [targetAmount, setTargetAmount] = useState(goal?.targetAmount ?? '');
  // Stored as the first of the month; `<input type="month">` wants YYYY-MM.
  const [targetDate, setTargetDate] = useState(
    goal ? monthValue(goal.targetDate) : '',
  );

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit({ name, targetAmount, targetDate });
  }

  return (
    <form onSubmit={submit} className="form-stack">
      <Field
        id="goal-name"
        label={t.name}
        name="name"
        type="text"
        autoComplete="off"
        required
        maxLength={100}
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Field
        id="goal-amount"
        label={t.targetAmount}
        name="targetAmount"
        type="text"
        inputMode="decimal"
        autoComplete="off"
        required
        value={targetAmount}
        onChange={(e) => setTargetAmount(e.target.value)}
      />
      <Field
        id="goal-date"
        label={t.targetMonth}
        name="targetDate"
        type="month"
        required
        value={targetDate}
        onChange={(e) => setTargetDate(e.target.value)}
      />
      <div className="form-actions">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          {t.cancel}
        </button>
        <button type="submit" className="btn-primary" disabled={pending}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function GoalRow({ goal, onEdit }: { goal: GoalDto; onEdit: () => void }) {
  const t = useDict(financesDict);
  const { locale } = useLocale();
  const remove = useDeleteGoal();

  return (
    <Tr id={`goal-row-${goal.id}`}>
      <Td emphasis>{goal.name}</Td>
      <Td numeric>{plnAmount(goal.targetAmount)}</Td>
      <Td>{plnMonth(goal.targetDate, locale)}</Td>
      <Td>
        <div className="row-actions">
          <button
            type="button"
            className="btn-icon-ghost"
            aria-label={t.editGoal(goal.name)}
            title={t.editGoal(goal.name)}
            onClick={onEdit}
          >
            <IconPencilSquare />
          </button>
          <DeleteButton
            label={t.deleteGoal(goal.name)}
            confirm={t.confirmDeleteGoal(goal.name)}
            onConfirm={() => remove.mutate(goal.id)}
          />
        </div>
      </Td>
    </Tr>
  );
}

export function Goals() {
  const t = useDict(financesDict);
  usePageTitle(t.goalsTitle);
  const { data: goals } = useGoals();
  const create = useCreateGoal();
  const update = useUpdateGoal();
  const [dialog, setDialog] = useState<
    null | { mode: 'add' } | { mode: 'edit'; goal: GoalDto }
  >(null);
  const close = () => setDialog(null);
  const editing = dialog?.mode === 'edit' ? dialog.goal : undefined;

  return (
    <>
      <PageHeader
        title={t.savingsGoals}
        actions={
          <button
            type="button"
            className="btn-primary-icon"
            aria-label={t.addGoal}
            title={t.addGoal}
            onClick={() => setDialog({ mode: 'add' })}
          >
            <IconPlus />
          </button>
        }
      />

      <DataTable
        columns={[
          { label: t.name },
          { label: t.target, align: 'center' },
          { label: t.targetMonth, align: 'center', priority: 'secondary' },
          { align: 'center' },
        ]}
        empty={t.noGoals}
      >
        {goals &&
          goals.length > 0 &&
          goals.map((goal) => (
            <GoalRow
              key={goal.id}
              goal={goal}
              onEdit={() => setDialog({ mode: 'edit', goal })}
            />
          ))}
      </DataTable>

      <Dialog
        open={dialog !== null}
        title={editing ? t.editGoal(editing.name) : t.addGoal}
        onClose={close}
      >
        <GoalForm
          key={editing?.id ?? 'new'}
          goal={editing}
          submitLabel={editing ? t.save : t.add}
          pending={editing ? update.isPending : create.isPending}
          onSubmit={(values) =>
            editing
              ? update.mutate(
                  { id: editing.id, ...values },
                  { onSuccess: close },
                )
              : create.mutate(values, { onSuccess: close })
          }
          onCancel={close}
        />
      </Dialog>
    </>
  );
}
